import { useCallback, useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Capacitor } from '@capacitor/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { addBreadcrumb } from '@sentry/react';

import { Geocoder } from '@ocx-app/lib/geolocation/Geocoder';
import { useOpenSystemSettings } from '@ocx-app/lib/open-native-settings/useOpenSystemSettings';
import { useInfoActionSheet } from '@ocx-app/components/modals/InfoActionSheet';
import { Coordinates, IStore } from '@ocx-app/modules/stores/types';
import { SEARCH_PARAM_KEY, useGetNavigationActions } from '@ocx-app/hooks/useGetNavigationActions';
import { useConfiguration } from '@ocx-app/modules/configuration/useConfiguration';

import { messages } from '../store-locator.messages';
import { SELECTED_STORE_ZOOM_LEVEL } from '../store-locator.constants';
import { useGetSpecificStoreFromUrlParams } from './use-get-specific-store-from-url-params.hook';

export const useMapNavigation = (
  // eslint-disable-next-line no-undef
  map: google.maps.Map | null,
  currentLocation: Coordinates,
  isLocationEstablished: boolean,
) => {
  const intl = useIntl();
  const { open: openInfoActionSheet } = useInfoActionSheet();
  const openNativeSettings = useOpenSystemSettings();
  const { pushToStoreLocator, replaceToStoreLocator } = useGetNavigationActions();
  const defaultLocation = useConfiguration('storeLocator.defaultLocation') as unknown as Coordinates;

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const specificStoreFromUrlParams = useGetSpecificStoreFromUrlParams();

  const currentOrDefaultLocation = isLocationEstablished ? currentLocation : defaultLocation;
  // position of map view (map center)
  const [targetLocation, setTargetLocation] = useState<Coordinates>(
    specificStoreFromUrlParams ? specificStoreFromUrlParams.urlParamCoordinates : currentOrDefaultLocation,
  );

  const [selectedStore, setSelectedStore] = useState<IStore | null>(null);
  const [locationNotFound, setLocationNotFound] = useState(false);

  const navigate = useNavigate();
  const search = searchParams.get(SEARCH_PARAM_KEY) || '';
  const isCanGoBack = useRef(false);

  const navigateWithSearch = useCallback(
    (value: string) => {
      const params = new URLSearchParams();
      if (value) {
        params.set(SEARCH_PARAM_KEY, value);
      }
      navigate({ search: params.toString() }, { replace: true });
    },
    [navigate],
  );

  const navigateToPosition = useCallback(
    (coordinates?: Coordinates) => {
      if (map === null) {
        return;
      }
      setLocationNotFound(false);

      if (coordinates) {
        setTargetLocation(coordinates);
        return;
      }

      const coords = map.getCenter();
      setTargetLocation(coords ? { lat: coords.lat(), lng: coords.lng() } : defaultLocation);
    },
    [defaultLocation, map],
  );

  const moveToCurrentLocation = useCallback(() => {
    navigateWithSearch('');
    navigateToPosition(currentLocation);
  }, [currentLocation, navigateToPosition, navigateWithSearch]);

  const navigateToListView = useCallback(() => {
    isCanGoBack.current = true;
    pushToStoreLocator({ list: true, search });
  }, [pushToStoreLocator, search]);

  const navigateToMapView = useCallback(() => {
    if (isCanGoBack.current) {
      navigate(-1);
    } else {
      isCanGoBack.current = true;
      replaceToStoreLocator({ search });
    }
  }, [replaceToStoreLocator, search, navigate]);

  const navigateToSearch = useCallback(
    async (search: string) => {
      setLocationNotFound(false);
      setSelectedStore(null);

      if (!search) {
        navigateToPosition();
        return;
      }

      if (!Geocoder.isGeocoderAvailable()) {
        addBreadcrumb({ message: '[Google Geocoder] is not available' });
        return;
      }

      const coords = await Geocoder.getCoordinates(search);

      if (coords === null) {
        setLocationNotFound(true);
        return;
      }

      navigateToPosition(coords);
    },
    [navigateToPosition],
  );

  const openLocationSettings = useCallback(
    () =>
      openInfoActionSheet({
        title: intl.formatMessage(messages['storeLocator:provideLocationPopupTitle']),
        description: intl.formatMessage(messages['storeLocator:provideLocationPopupDescription']),
        actionButtonProps: Capacitor.isNativePlatform()
          ? {
              onActionButtonClick: () => openNativeSettings('application_details'),
              actionButtonText: intl.formatMessage(messages['storeLocator:provideLocationPopupConfirmButton']),
            }
          : undefined,
        isServiceLinkEnabled: true,
      }),
    [intl, openInfoActionSheet, openNativeSettings],
  );

  const handleSelectStore = useCallback(
    (store: IStore | null) => {
      if (map === null) {
        return;
      }
      if (store === null) {
        setSelectedStore(null);
        return;
      }

      setSelectedStore(store);

      const coordinates = { lat: store.lat, lng: store.lng };
      navigateToPosition(coordinates);
      map.panTo(coordinates);
      const mapZoom = map.getZoom();
      if (!mapZoom || mapZoom < SELECTED_STORE_ZOOM_LEVEL) {
        map.setZoom(SELECTED_STORE_ZOOM_LEVEL);
      }
    },
    [map, navigateToPosition],
  );

  useEffect(() => {
    if (search) {
      navigateToSearch(search);
    }
  }, [navigateToSearch, search]);

  return {
    search,
    selectedStore,
    targetLocation,
    navigateToPosition,
    handleSelectStore,
    moveToCurrentLocation,
    navigateToListView,
    navigateToMapView,
    navigateWithSearch,
    openLocationSettings,
    locationNotFound,
    setSelectedStore,
    specificStoreUUID: specificStoreFromUrlParams?.specificStoreUUID,
  };
};
